import { useEffect, useState } from "react";

/**
 * 要素の viewport 上の `left` / `top` / `width` を追跡するフック。
 *
 * - `ResizeObserver` で要素自身と親要素のサイズ変化に追従
 * - `window resize` イベントでビューポート幅変更にも追従
 * - 祖先要素の CSS `transform` transition 完了でも再計測
 *   （SP の `TwoColumnLayout` が `translateX` で右カラムをスライドインさせる際、
 *    `ResizeObserver` では検知できない `transform` 由来の位置変化を捕捉するため）
 * - マウント直後の数フレームだけ rAF で再計測（transition 中のマウントや
 *   画像の遅延 reflow で初回 rect が画面外になるケースの保険）
 *
 * `position: fixed` の子要素を「ある DOM 要素の真下に重ねる」ようなレイアウト
 * （DM 右カラムの ActionPanel など）で利用する。
 */
export interface ElementRect {
  left: number;
  /** viewport 上端からの距離。カード領域の上端基準などに使う。 */
  top: number;
  width: number;
}

const MOUNT_RAF_TICKS = 30; // ~500ms ぶんのフォールバック再計測

export const useElementRect = (
  ref: React.RefObject<HTMLDivElement | null>,
): ElementRect => {
  const [rect, setRect] = useState<ElementRect>({ left: 0, top: 0, width: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const sync = () => {
      const r = el.getBoundingClientRect();
      setRect((prev) =>
        prev.left === r.left && prev.top === r.top && prev.width === r.width
          ? prev
          : { left: r.left, top: r.top, width: r.width },
      );
    };

    sync();

    let frameCount = 0;
    let rafId = requestAnimationFrame(function tick() {
      sync();
      frameCount += 1;
      if (frameCount < MOUNT_RAF_TICKS) rafId = requestAnimationFrame(tick);
    });

    const ro = new ResizeObserver(sync);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    window.addEventListener("resize", sync);

    // 祖先要素の transform transition 完了で再計測する。
    // `propertyName` を絞ることでボタン hover の color transition などでの
    // 無駄発火を抑制する。
    const onTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName === "transform") sync();
    };
    document.addEventListener("transitionend", onTransitionEnd, true);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      window.removeEventListener("resize", sync);
      document.removeEventListener("transitionend", onTransitionEnd, true);
    };
  }, [ref]);

  return rect;
};

export default useElementRect;
